import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { trackFacebookEvent } from '../providers/FacebookPixel';
import { AD_TRACKED_EVENTS } from './ad-events';
import { sendGAEvent } from '../providers/GoogleAnalytics';
import { TIKTOK_TRACKED_EVENTS, trackTikTokEvent } from '../providers/TikTokPixel';
import posthog from 'posthog-js';
import { useCookieConsent } from '@/app/stores/cookie-consent-store';
import { createEventTracker, type AnalyticsEvent } from '@puzzlio/core';
import { deviceStorage } from '@/app/lib/deviceStorage';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Whether a rejected `navigator.share()` was the person dismissing the sheet rather than a failure.
 *
 * The Web Share API reports a cancelled share as a rejected promise with an `AbortError`, the same
 * shape as a genuine fault. Logging it as an error is wrong twice over: nothing broke, and in
 * development it trips Next.js's error overlay over an ordinary "no thanks".
 *
 * Matched on `name` alone rather than `instanceof DOMException`: what browsers reject with here is
 * not uniform, and an error crossing a realm (an iframe, a polyfill) fails the instanceof while
 * still carrying the right name. `NotAllowedError` is deliberately excluded — that one means the
 * call was refused, usually for want of user activation, which is a fault worth seeing.
 */
export function isShareCancellation(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

export function isStandaloneMode(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Sends an analytics event to Posthog and Google Analytics, plus Facebook, Google Ads and TikTok for
 * whitelisted events.
 *
 * `skipPosthog` is for events captured server-side instead (see lib/posthog-server.ts), which would
 * otherwise be counted twice. The ad pixels still need the browser: they read fbp/fbc cookies and
 * click ids that a server call can't reproduce without each vendor's conversions API.
 *
 * `email` is for TikTok's advanced matching and goes nowhere else -- it reaches TikTok as a SHA-256
 * hash, and the other platforms have no business receiving an address they didn't ask for.
 */
export const trackAllAnalyticsEvents = (
  eventName: string,
  properties: Record<string, any>,
  { skipPosthog = false, email }: { skipPosthog?: boolean; email?: string } = {}
) => {
  const analyticEnabled = process.env.NODE_ENV === 'production';
  const { isAccepted } = useCookieConsent.getState();

  if (!analyticEnabled || !isAccepted) return;

  if (!skipPosthog) posthog.capture(eventName, properties);
  sendGAEvent(eventName, properties);

  if (AD_TRACKED_EVENTS.has(eventName)) {
    trackFacebookEvent(eventName, properties);
  }

  if (TIKTOK_TRACKED_EVENTS.has(eventName)) {
    trackTikTokEvent(eventName, properties, email);
  }
};

const tracker = createEventTracker({
  storage: deviceStorage,
  send: ({ name, properties }) => trackAllAnalyticsEvents(name, properties),
});

/**
 * Sends an event built by `@puzzlio/core/analytics`, honouring the emission limit it carries.
 *
 * `onSent` runs only when the event actually went — for a limited event that means only on the
 * emission that counted, which is what the Google Ads conversion on the first-ever completion wants.
 */
export const trackEvent = (event: AnalyticsEvent, { onSent }: { onSent?: () => void } = {}) =>
  tracker.track(event, onSent);

/** Drops the stored counters under a prefix, keeping the one named by `except`. */
export const forgetTrackedEvents = (prefix: string, options?: { except?: string }) => tracker.forget(prefix, options);

export function hasLocalStorageKeyMatching(regex: RegExp): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && regex.test(key)) {
      return localStorage.getItem(key);
    }
  }

  return null;
}

export function getNextSundayMidnight(): Date {
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysUntilSunday = (7 - currentDay) % 7;

  const nextSunday = new Date(now);
  nextSunday.setDate(now.getDate() + daysUntilSunday);
  nextSunday.setHours(23, 59, 59, 999); // Set to end of Sunday

  return nextSunday;
}
